/** 백엔드 IXD 매칭 미리보기를 비교 화면의 chunk/행 lookup으로 정규화한다. */

import type { IxdChunk, IxdDlView } from "@/apis/ixd-editor/types";
import type {
  IxdMatchPreviewEntry,
  IxdMatchPreviewResult,
  WorkbookMatchTable,
} from "@/apis/ixd-value-sync/types";

export type MatchedWorkbookTable = WorkbookMatchTable;

/** IXD 표(행) 식별 키 — chunk id + dlView index */
export const buildRowKey = (chunkId: string, dlViewIndex: number) =>
  `${chunkId}-dl-${dlViewIndex}`;

export interface IxdMatchViewModel {
  chunks: IxdChunk[];
  matches: Map<string, MatchedWorkbookTable>;
  /** 목차(chunk id) → IXD 표가 없어 그 목차 섹션 끝에 그리는 워크북 표 */
  workbookOnlyByChunkId: Map<string, WorkbookMatchTable[]>;
  /** 어느 목차에도 못 넣은 워크북 표 — 페이지 맨 아래 별도 목록 */
  workbookOnly: WorkbookMatchTable[];
}

const resolveEntryTocId = (
  entry: IxdMatchPreviewEntry,
  validTocIds: ReadonlySet<string>,
  tocIdByRoleKey: ReadonlyMap<string, string>,
): string | undefined => {
  if (!entry.ixd) return undefined;
  const explicitTocId = entry.ixd.tocId;
  if (explicitTocId && validTocIds.has(explicitTocId)) {
    return explicitTocId;
  }
  return tocIdByRoleKey.get(entry.ixd.roleKey);
};

export const buildIxdMatchViewModel = (
  preview: IxdMatchPreviewResult | undefined,
  isConsolidated: boolean,
  includeUnassignedWorkbookOnly = false,
): IxdMatchViewModel => {
  const matches = new Map<string, MatchedWorkbookTable>();
  const matchedWorkbookTableIds = new Set<string>();
  const workbookOnlyByChunkId = new Map<string, WorkbookMatchTable[]>();
  if (!preview) {
    return { chunks: [], matches, workbookOnlyByChunkId, workbookOnly: [] };
  }

  const selectedToc = preview.ixdToc.filter(
    (toc) => toc.isConsolidated === isConsolidated,
  );
  const selectedTocIds = new Set(selectedToc.map((toc) => toc.id));
  const validTocIds = new Set(preview.ixdToc.map((toc) => toc.id));
  const tocIdByRoleKey = new Map<string, string>();
  for (const toc of preview.ixdToc) {
    for (const roleKey of toc.roleKeys) {
      // 기존 find 동작처럼 같은 roleKey가 여러 TOC에 있으면 첫 번째를 사용한다.
      if (!tocIdByRoleKey.has(roleKey)) tocIdByRoleKey.set(roleKey, toc.id);
    }
  }
  const entriesByTocId = new Map<string, IxdMatchPreviewEntry[]>();
  for (const entry of preview.entries) {
    if (!entry.ixd?.dlView) continue;
    const tocId = resolveEntryTocId(entry, validTocIds, tocIdByRoleKey);
    if (!tocId || !selectedTocIds.has(tocId)) continue;
    const entries = entriesByTocId.get(tocId) ?? [];
    entries.push(entry);
    entriesByTocId.set(tocId, entries);
  }

  const allChunks = selectedToc.map<IxdChunk>((toc) => {
    const entries = entriesByTocId.get(toc.id) ?? [];
    const dlViews = entries.flatMap((entry, dlViewIndex) => {
      const dlView = entry.ixd?.dlView;
      if (!dlView) return [];
      if (entry.workbook) {
        matches.set(buildRowKey(toc.id, dlViewIndex), entry.workbook);
        matchedWorkbookTableIds.add(entry.workbook.tableId);
      }
      return [dlView];
    });
    return { id: toc.id, title: toc.title, dlViews };
  });

  const selectedRoleKeys = new Set(selectedToc.flatMap((toc) => toc.roleKeys));
  const allTocRoleKeys = new Set(preview.ixdToc.flatMap((toc) => toc.roleKeys));
  const workbookOnly = preview.entries.flatMap((entry) => {
    if (
      !entry.workbook ||
      matchedWorkbookTableIds.has(entry.workbook.tableId)
    ) {
      return [];
    }
    // 백엔드가 정한 소속 목차가 이 화면의 TOC에 있으면 그 섹션 끝에 그린다.
    const tocId = entry.workbook.tocId;
    if (tocId && selectedTocIds.has(tocId)) {
      const tables = workbookOnlyByChunkId.get(tocId) ?? [];
      tables.push(entry.workbook);
      workbookOnlyByChunkId.set(tocId, tables);
      return [];
    }
    const belongsToSelectedToc = selectedRoleKeys.has(entry.workbook.roleKey);
    const isUnassigned = !allTocRoleKeys.has(entry.workbook.roleKey);
    return belongsToSelectedToc ||
      (includeUnassignedWorkbookOnly && isUnassigned)
      ? [entry.workbook]
      : [];
  });

  // IXD 표가 하나도 없는 목차도 워크북 표가 붙으면 섹션으로 남긴다 — 텍스트만 있는 주석에
  // 워크북 표가 있는 경우가 실제로 있다.
  const chunks = allChunks.filter(
    (chunk) => chunk.dlViews.length > 0 || workbookOnlyByChunkId.has(chunk.id),
  );

  return { chunks, matches, workbookOnlyByChunkId, workbookOnly };
};

export type CompareRow =
  | {
      kind: "ixd";
      key: string;
      chunk: IxdChunk;
      dlView: IxdDlView;
      dlViewIndex: number;
      match: MatchedWorkbookTable | undefined;
      /** 목차의 첫 행이면 chunk id. 사이드바 이동·스크롤 스파이가 찾는 앵커라 목차당 정확히 하나 */
      anchorChunkId?: string;
    }
  | {
      kind: "workbook";
      key: string;
      table: WorkbookMatchTable;
      /** 이 표가 붙은 목차 제목 — 표 이름에 목차 번호를 붙일 때 쓴다(꼬리 목록은 없음) */
      chunkTitle?: string;
      anchorChunkId?: string;
    }
  | {
      /** 표도 문단도 없는 목차 — 목차 클릭이 갈 곳(앵커)과 "표 없음" 표시를 위해 남긴다 */
      kind: "empty";
      key: string;
      chunk: IxdChunk;
      anchorChunkId: string;
    };

/**
 * chunks와 목차별 워크북 전용 표를 화면 행 목록으로 평탄화한다. 각 목차는 IXD 행 뒤에
 * 워크북 행이 오고, 종류와 무관하게 첫 행이 목차 앵커를 맡는다 — 어떤 chunk가 chunks에
 * 남는지(buildIxdMatchViewModel의 filter)와 앵커 배정이 여기 한 파일에서 함께 바뀐다.
 */
export const buildCompareRows = (
  chunks: IxdChunk[],
  matches: ReadonlyMap<string, MatchedWorkbookTable>,
  workbookOnlyByChunkId: ReadonlyMap<string, WorkbookMatchTable[]>,
): CompareRow[] =>
  chunks.flatMap((chunk) => {
    const ixdRows: CompareRow[] = chunk.dlViews.map((dlView, dlViewIndex) => ({
      kind: "ixd",
      key: buildRowKey(chunk.id, dlViewIndex),
      chunk,
      dlView,
      dlViewIndex,
      match: matches.get(buildRowKey(chunk.id, dlViewIndex)),
    }));
    const workbookRows: CompareRow[] = (
      workbookOnlyByChunkId.get(chunk.id) ?? []
    ).map((table) => ({
      kind: "workbook",
      key: `${chunk.id}-wb-${table.tableId}`,
      table,
      chunkTitle: chunk.title,
    }));
    const rows = [...ixdRows, ...workbookRows];
    // 내용이 하나도 없는 목차도 행을 하나 남긴다 — 없으면 목차를 눌러도 갈 곳이 없어
    // 아무 일도 일어나지 않고, 문서에 그 목차가 있다는 사실도 화면에서 사라진다.
    if (rows.length === 0) {
      return [
        {
          kind: "empty" as const,
          key: `${chunk.id}-empty`,
          chunk,
          anchorChunkId: chunk.id,
        },
      ];
    }
    rows[0] = { ...rows[0], anchorChunkId: chunk.id };
    return rows;
  });
